<template>
  <section class="settings-page" :dir="direction">
    <header>
      <button type="button" :aria-label="$t('settingsUi.back')" data-analytics="settings.close" @click="$emit('close')"><i class="el-icon-arrow-left" /></button>
      <div><small>{{ $t("settingsUi.eyebrow") }}</small><h1>{{ $t("settingsUi.title") }}</h1></div>
      <span />
    </header>
    <div class="settings-scroll">
      <div class="identity-card">
        <platform-avatar :src="userAvatar" :alt="$t('centerDetail.avatarAlt')" />
        <div><strong>{{ userName }}</strong><small>{{ maskedEmail }}</small></div>
        <i class="el-icon-lock" />
      </div>

      <section class="settings-section">
        <h2>{{ $t("settingsUi.account") }}</h2>
        <button type="button" class="settings-card" data-analytics="settings.profile" @click="$emit('open-profile')">
          <span class="row-icon profile"><i class="el-icon-edit-outline" /></span>
          <div><strong>{{ $t("center.editProfile") }}</strong><small>{{ $t("center.profileTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <div class="settings-card locale-row" data-analytics="header.locale">
          <span class="row-icon"><i class="el-icon-map-location" /></span>
          <div><strong>{{ $t("settingsUi.languageCountry") }}</strong><small>{{ $t("settingsUi.languageCountryTip") }}</small></div>
          <locale-switcher />
        </div>
        <button type="button" class="settings-card" data-analytics="settings.notifications" @click="$emit('open-notifications')">
          <span class="row-icon"><i class="el-icon-bell" /></span>
          <div><strong>{{ $t("settingsUi.notifications") }}</strong><small>{{ $t("settingsUi.notificationsTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button v-if="showPWAInstall" type="button" class="settings-card" data-analytics="settings.pwa_install" @click="$emit('open-pwa-install')">
          <span class="row-icon pwa-install"><i class="el-icon-mobile-phone" /></span>
          <div><strong>{{ $t("pwa.install.settingsTitle") }}</strong><small>{{ $t("pwa.install.settingsTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button type="button" class="settings-card" data-analytics="settings.security" @click="securityVisible = true">
          <span class="row-icon security"><i class="el-icon-key" /></span>
          <div><strong>{{ $t("securityUi.title") }}</strong><small>{{ $t("securityUi.currentEmail") }} · {{ maskedEmail }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button type="button" class="settings-card" data-analytics="settings.privacy" @click="privacyVisible = true">
          <span class="row-icon privacy-control"><i class="el-icon-view" /></span>
          <div><strong>{{ $t("privacyManagement.title") }}</strong><small>{{ $t("privacyManagement.entryTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button type="button" class="settings-card" data-analytics="settings.streamer_access" @click="showStreamerApplicationNotice">
          <span class="row-icon streamer"><i class="el-icon-microphone" /></span>
          <div><strong>{{ $t("settingsUi.streamerAccess") }}</strong><small>{{ $t("settingsUi.streamerAccessTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button type="button" class="settings-card" data-analytics="settings.guild" @click="guildVisible = true">
          <span class="row-icon guild"><i class="el-icon-office-building" /></span>
          <div><strong>{{ $t("guildUi.settingsTitle") }}</strong><small>{{ $t("guildUi.settingsTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button type="button" class="settings-card" data-analytics="settings.support" @click="supportVisible = true">
          <span class="row-icon support"><i class="el-icon-service" /></span>
          <div><strong>{{ $t("supportUi.settingsTitle") }}</strong><small>{{ $t("supportUi.settingsTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
      </section>

      <section class="settings-section">
        <h2>{{ $t("settingsUi.legal") }}</h2>
        <button type="button" class="settings-card" data-analytics="settings.terms" @click="$emit('open-legal', 'terms')">
          <span class="row-icon terms"><i class="el-icon-document" /></span>
          <div><strong>{{ $t("settingsUi.terms") }}</strong><small>{{ $t("settingsUi.termsTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button type="button" class="settings-card" data-analytics="settings.privacy_policy" @click="$emit('open-legal', 'privacy')">
          <span class="row-icon privacy"><i class="el-icon-lock" /></span>
          <div><strong>{{ $t("settingsUi.privacy") }}</strong><small>{{ $t("settingsUi.privacyTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
        <button type="button" class="settings-card" data-analytics="settings.community" @click="$emit('open-legal', 'community')">
          <span class="row-icon community"><i class="el-icon-guide" /></span>
          <div><strong>{{ $t("settingsUi.community") }}</strong><small>{{ $t("settingsUi.communityTip") }}</small></div>
          <i class="el-icon-arrow-right" />
        </button>
      </section>

      <button type="button" class="logout-button" data-analytics="settings.logout" @click="confirmLogout">
        <i class="el-icon-switch-button" />
        <span><strong>{{ $t("settingsUi.logout") }}</strong><small>{{ $t("settingsUi.logoutTip") }}</small></span>
      </button>
      <p class="brand-foot">STARLIVE · ACCOUNT CENTER</p>
    </div>
    <security-settings v-if="securityVisible" @close="securityVisible = false" @email-updated="handleEmailUpdated" />
    <guild-membership-center
      v-if="guildVisible"
      :initial-request-id="guildRequestId"
      @close="closeGuild"
      @membership-changed="$emit('guild-membership-changed', $event)"
    />
    <customer-support-center v-if="supportVisible" :initial-ticket-id="supportTicketId" @close="closeSupport" />
    <privacy-management
      v-if="privacyVisible"
      @close="privacyVisible = false"
      @data-erased="$emit('data-erased')"
      @account-deletion-requested="$emit('account-deletion-requested')"
    />
  </section>
</template>

<script>
import LocaleSwitcher from "./LocaleSwitcher.vue"
import SecuritySettings from "./SecuritySettings.vue"
import GuildMembershipCenter from "./GuildMembershipCenter.vue"
import CustomerSupportCenter from "./CustomerSupportCenter.vue"
import PrivacyManagement from "./PrivacyManagement.vue"
import { localeState } from "../i18n"
import { DEFAULT_AVATAR_URL } from "../utils/media"
import { canShowInstallEntry } from "../utils/pwa"

export default {
  name: "AccountSettings",
  components: { CustomerSupportCenter, GuildMembershipCenter, LocaleSwitcher, PrivacyManagement, SecuritySettings },
  props: {
    user: { type: Object, default: () => ({}) },
    initialPanel: { type: String, default: "" },
    guildRequestId: { type: Number, default: 0 },
    supportTicketId: { type: Number, default: 0 }
  },
  data() {
    return { securityVisible: false, guildVisible: false, supportVisible: false, privacyVisible: false, updatedEmail: "" }
  },
  computed: {
    showPWAInstall() {
      return canShowInstallEntry()
    },
    direction() {
      return String(localeState.language).toLowerCase().startsWith("ar") ? "rtl" : "ltr"
    },
    userName() {
      return this.user.nickname || this.user.name || "StarLive"
    },
    userEmail() {
      return this.updatedEmail || this.user.email || ""
    },
    maskedEmail() {
      const parts = String(this.userEmail).split("@")
      if (parts.length !== 2) return ""
      const account = parts[0]
      const visible = account.slice(0, Math.min(2, account.length))
      return `${visible}${"*".repeat(Math.max(3, Math.min(8, account.length - visible.length)))}@${parts[1]}`
    },
    userAvatar() {
      return this.user.avatar_url || this.user.avatar || DEFAULT_AVATAR_URL
    }
  },
  methods: {
    showStreamerApplicationNotice() {
      const email = process.env.VUE_APP_GUILD_CONTACT_EMAIL || "xxxxx"
      this.$notice.alert(
        this.$t("settingsUi.streamerAccessNotice", { email }),
        this.$t("settingsUi.streamerAccessNoticeTitle"),
        { type: "info", confirmButtonText: this.$t("common.confirm") }
      ).catch(() => {})
    },
    handleEmailUpdated(data) {
      this.updatedEmail = data.email || ""
      this.$emit("email-updated", data)
    },
    confirmLogout() {
      this.$notice.confirm(this.$t("settingsUi.logoutConfirm"), this.$t("settingsUi.logout"))
        .then(() => this.$emit("logout"))
        .catch(() => {})
    },
    closeGuild() {
      this.guildVisible = false
      this.$emit("guild-closed")
    },
    closeSupport() {
      this.supportVisible = false
      this.$emit("support-closed")
    }
  },
  mounted() {
    if (this.initialPanel === "streamer-access") this.showStreamerApplicationNotice()
    if (this.initialPanel === "guild") this.guildVisible = true
    if (this.initialPanel === "support") this.supportVisible = true
  },
  watch: {
    initialPanel(value) {
      if (value === "streamer-access") this.showStreamerApplicationNotice()
      if (value === "guild") this.guildVisible = true
      if (value === "support") this.supportVisible = true
    }
  }
}
</script>

<style scoped>
.settings-page{position:fixed;z-index:4000;top:0;bottom:0;left:50%;width:min(100vw,430px);transform:translateX(-50%);display:flex;flex-direction:column;overflow:hidden;color:#f8f5ef;background:radial-gradient(circle at 90% 0,rgba(255,61,87,.25),transparent 31%),radial-gradient(circle at 0 70%,rgba(139,92,246,.12),transparent 31%),#0d0e11}.settings-page>header{height:calc(82px + env(safe-area-inset-top));padding:env(safe-area-inset-top) 18px 0;display:grid;grid-template-columns:40px 1fr 40px;align-items:center;border-bottom:1px solid rgba(255,255,255,.07)}header button{width:36px;height:36px;border:1px solid #363840;border-radius:50%;background:#191a1f;color:#fff}header div{text-align:center}header small{display:block;color:#ff5368;font-size:8px;letter-spacing:2.4px}header h1{margin:4px 0 0;font-size:23px}.settings-scroll{flex:1;overflow-y:auto;padding:18px 16px calc(28px + env(safe-area-inset-bottom));scrollbar-width:none}.settings-scroll::-webkit-scrollbar{display:none}.identity-card{display:flex;align-items:center;gap:13px;padding:16px;border:1px solid rgba(255,255,255,.1);border-radius:20px;background:linear-gradient(145deg,rgba(255,255,255,.09),rgba(255,255,255,.025));box-shadow:0 18px 40px rgba(0,0,0,.18)}.identity-card img{width:48px;height:48px;border:2px solid rgba(255,255,255,.15);border-radius:50%;object-fit:cover}.identity-card div{min-width:0;flex:1}.identity-card strong,.identity-card small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.identity-card strong{font-size:16px}.identity-card small{margin-top:5px;color:#85878e;font-size:10px}.identity-card>i{color:#ff7788}.settings-section{margin-top:24px}.settings-section h2{margin:0 3px 10px;color:#868890;font-size:10px;letter-spacing:1.5px;text-transform:uppercase}.settings-card{width:100%;min-height:70px;display:flex;align-items:center;gap:12px;margin-bottom:9px;padding:12px;border:1px solid #282a30;border-radius:17px;background:#17181d;color:#fff;text-align:start}.settings-card>div{min-width:0;flex:1}.settings-card strong,.settings-card small{display:block}.settings-card strong{font-size:13px}.settings-card small{margin-top:5px;color:#81838b;font-size:10px;line-height:1.4}.settings-card>i:last-child{color:#62646b}.row-icon{width:40px;height:40px;display:grid;place-items:center;flex:none;border-radius:13px;background:#292a30;color:#ff6579;font-size:17px}.row-icon.pwa-install{color:#20e2d7}.row-icon.terms{color:#ffb45c}.row-icon.privacy{color:#8ca9ff}.locale-row ::v-deep .locale-trigger{min-width:48px;border-color:#34363d!important;background:#222329!important}.logout-button{width:100%;display:flex;align-items:center;gap:13px;margin-top:28px;padding:15px 17px;border:1px solid rgba(255,69,93,.25);border-radius:17px;background:rgba(255,64,89,.08);color:#ff7184;text-align:start}.logout-button>i{font-size:19px}.logout-button span{min-width:0}.logout-button strong,.logout-button small{display:block}.logout-button small{margin-top:4px;color:#9d6d75;font-size:10px}.brand-foot{margin:28px 0 0;color:#51535a;font-size:8px;letter-spacing:2px;text-align:center}[dir="rtl"] header button i,[dir="rtl"] .settings-card>i:last-child{transform:rotate(180deg)}
.row-icon.security{color:#6ddeaa}
.row-icon.profile{color:#f0c778}
.row-icon.community{color:#56d8d0}
.row-icon.guild{color:#58d7ae}
.row-icon.support{color:#ff7185}
.row-icon.privacy-control{color:#62dec5}
.row-icon.streamer{color:#ff7185}
</style>
